// Copyright (c) 2022 by Move Closer

import isString from 'lodash/isString'

import { logger } from '../../../support'

import { FileType } from '../contracts'
import { isFile } from './file'

/**
 * Determines whether the passed-in `input` object correctly implements the `ImageFile` interface.
 *
 * @typeGuard
 */
export const isImageFile = (input: unknown): input is ImageData => {
  if (typeof input === 'undefined') {
    logger('isImageFile(): The input is undefined', 'debug')
    return false
  }

  // Validate the basic `File` interface compliance.
  if (typeof input !== 'object' || input === null || !isFile(input, FileType.Image)) {
    const message: string = 'isImageFile(): The input object did NOT pass the [isFile()] type guard!'
    logger([message, input], 'error')
    return false
  }

  // Remove (override) the type inference that came from the `isFile()` type guard.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const candidate = input as any

  // Validate the presence of the required keys (not covered by the `isFile()` type guard).
  const requiredKeys = ['srcset']
  for (const requiredKey of requiredKeys) {
    if (typeof candidate[requiredKey] === 'undefined') {
      const message: string = `isImageFile(): [${requiredKey}] key is missing!`
      logger([message, candidate], 'error')
      return false
    }
  }

  // Validate the value type of the `srcset` key.
  if (!Array.isArray(candidate.srcset)) {
    const message: string = `isImageFile(): Expected the [candidate.srcset] to be an [array], but got [${typeof candidate.srcset}]!`
    logger([message, candidate], 'error')
    return false
  }

  // Validate the value of every item in the `candidate.srcset` array.
  for (const [index, item] of candidate.srcset.entries()) {
    // Validate the string keys.
    const stringKeys = ['url']
    for (const stringKey of stringKeys) {
      if (!isString(item[stringKey])) {
        const message: string = `isImageFile(): Expected [candidate.srcset[${index}].${stringKey}] to be a [string], but got [${typeof item[stringKey]}]!`
        logger([message, candidate], 'error')
        return false
      }

      if (item[stringKey].length === 0) {
        const message: string = `isImageFile(): Value of the [candidate.srcset[${index}].${stringKey}] can NOT be empty!`
        logger([message, candidate], 'error')
        return false
      }
    }

    // Validate the number keys.
    const numberKeys = ['height', 'width']
    for (const numberKey of numberKeys) {
      const isValidNumber: boolean = isFinite(item[numberKey])
      const isNull: boolean = item[numberKey] === null

      if (!isValidNumber && !isNull) {
        const message: string = `isImageFile(): Expected [candidate.srcset[${index}].${numberKey}] to be a [number], but got [${typeof item[numberKey]}]!`
        logger(message, 'error')
        logger([`[candidate.srcset[${index}]:`, JSON.parse(JSON.stringify(item))])
        return false
      }
    }

    // Validate the `width` key.
    if (item.width <= 0) {
      const message: string = `isImageFile(): Value of the [candidate.srcset[${index}].width] can NOT be less than or equal [0]!`
      logger(message, 'error')
      logger([`[candidate.srcset[${index}]:`, JSON.parse(JSON.stringify(item))])
      return false
    }
  }

  return true
}
